import { cloneDeep, isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useCallback, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import {
  WEBSITE_BUILDER_TABLE_MAX_CELLS,
  WEBSITE_BUILDER_TABLE_MAX_ROWS,
  WEBSITE_BUILDER_TABLE_MIN_CELLS,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import type {
  IWebsiteBuilderTable,
  IWebsiteBuilderTableBodyCell,
  IWebsiteBuilderTableBodyRow,
  IWebsiteBuilderTableHeadCell,
  IWebsiteBuilderTableSettings,
  TWebsiteBuilderTableCell,
  TWebsiteBuilderTableCellValue,
  TWebsiteBuilderTableRow,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import { websiteBuilderUpdateContainerSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { ITableBuilderState, TTableBuilderSelection } from "../TableBuilder";
import { TTableBuilderDragLayerContext } from "./useTableBuilderDragLayerContext";
import { XYCoord } from "react-dnd";
import { IWebsiteBuilderSectionEntityViewBoundingBox } from "../../designSpace/views/hooks/sectionEntity/helpers/sectionEntityViewBoundingBox";
import { TTableBuilderDomNodeRefMap } from "./useTableBuilderDomNodeRefMapRegistration";

interface IUseTableBuilderOperationsArgs {
  dragLayerContext: TTableBuilderDragLayerContext;
  containerBoundingBox: IWebsiteBuilderSectionEntityViewBoundingBox,
  domNodeRefMap: TTableBuilderDomNodeRefMap,
  tableNode: IWebsiteBuilderTable;
  tableRef: React.MutableRefObject<HTMLTableElement>,
  setState: Dispatch<SetStateAction<ITableBuilderState>>;
}

export type TTableBuilderSetRowColorProperty = keyof Pick<TWebsiteBuilderTableRow, "color" | "backgroundColor">;

type TTableBuilderSetSelection = (selection: TTableBuilderSelection) => void;
type TTableBuilderSetRowColor = (rowId: string, property: TTableBuilderSetRowColorProperty, color: string) => void;
type TTableBuilderSetCellValue = (id: string, value: TWebsiteBuilderTableCellValue) => void;
type TTableBuilderClearColumn = (columnIndex: number) => void;
type TTableBuilderClearRow = (rowId: string) => void;
type TTableBuilderDeleteColumn = (columnIndex: number) => void;
type TTableBuilderDeleteRow = (rowId: string) => void;
type TTableBuilderDuplicateColumn = (columnIndex: number) => void;
type TTableBuilderDuplicateRow = (rowId: string) => void;
type TTableBuilderInsertColumn = (columnIndex: number, placement: "before" | "after") => void;
type TTableBuilderInsertRow = (rowId: string, placement: "above" | "below") => void;
type TTableBuilderInsertRowBelowHead = () => void;
type TTableBuilderHoverColumn = (columnIndex: number) => void;
type TTableBuilderBlurColumn = (columnIndex: number) => void;
type TTableBuilderHoverRow = (rowId: string) => void;
type TTableBuilderBlurRow = (rowId: string) => void;
type TTableBuilderMoveRow = () => void;
type TTableBuilderMoveColumn = () => void;

export interface ITableBuilderOperations {
  setSelection: TTableBuilderSetSelection;
  setRowColor: TTableBuilderSetRowColor;
  setCellValue: TTableBuilderSetCellValue;
  clearColumn: TTableBuilderClearColumn;
  clearRow: TTableBuilderClearRow;
  deleteColumn: TTableBuilderDeleteColumn;
  deleteRow: TTableBuilderDeleteRow;
  duplicateColumn: TTableBuilderDuplicateColumn;
  duplicateRow: TTableBuilderDuplicateRow;
  insertColumn: TTableBuilderInsertColumn;
  insertRow: TTableBuilderInsertRow;
  insertRowBelowHead: TTableBuilderInsertRowBelowHead;
  hoverColumn: TTableBuilderHoverColumn;
  blurColumn: TTableBuilderBlurColumn;
  hoverRow: TTableBuilderHoverRow;
  blurRow: TTableBuilderBlurRow;
  moveRow: TTableBuilderMoveRow;
  moveColumn: TTableBuilderMoveColumn;
}

export function useTableBuilderOperations(args: IUseTableBuilderOperationsArgs): ITableBuilderOperations {
  const { dragLayerContext, containerBoundingBox, domNodeRefMap, tableNode, tableRef, setState } = args;

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const tableNodeSettings = tableNode.container.settings;

  function sanitizeBodyRow(row: IWebsiteBuilderTableBodyRow): IWebsiteBuilderTableBodyRow {
    return {
      ...cloneDeep(row),
      id: crypto.randomUUID(),
      cells: row.cells.map(cell => ({
        ...cloneDeep(cell),
        id: crypto.randomUUID(),
      })),
    };
  }

  function sanitizeCell(cell: IWebsiteBuilderTableBodyCell): IWebsiteBuilderTableBodyCell;
  function sanitizeCell(cell: IWebsiteBuilderTableHeadCell): IWebsiteBuilderTableHeadCell;
  function sanitizeCell(cell: TWebsiteBuilderTableCell): TWebsiteBuilderTableCell {
    return {
      ...cloneDeep(cell),
      id: crypto.randomUUID(),
    };
  }

  function generateEmptyHeadCell(): IWebsiteBuilderTableHeadCell {
    return {
      id: crypto.randomUUID(),
      type: "head",
      value: { type: "text", content: "" },
    };
  }

  function generateEmptyBodyCell(): IWebsiteBuilderTableBodyCell {
    return {
      id: crypto.randomUUID(),
      type: "body",
      value: { type: "text", content: "" },
    };
  }

  function generateEmptyBodyRow(cellCount: number): IWebsiteBuilderTableBodyRow {
    return {
      id: crypto.randomUUID(),
      type: "body",
      cells: Array.from({ length: cellCount }, generateEmptyBodyCell),
    };
  }

  const dispatchTableSettingsUpdate = useCallback(
    (prevSettings: IWebsiteBuilderTableSettings, nextSettings: IWebsiteBuilderTableSettings) => {
      dispatch(
        websiteBuilderUpdateContainerSettings(activePageId, deviceView, tableNode.id, {
          id: tableNode.id,
          prevContainerSettings: {
            type: "table",
            settings: prevSettings,
          },
          nextContainerSettings: {
            type: "table",
            settings: nextSettings,
          },
        }),
      );
    },
    [activePageId, deviceView, tableNode.id],
  );

  const setSelection: TTableBuilderSetSelection = useCallback(selection => {
    setState(prev => ({ ...prev, selection }));
  }, []);

  const setRowColor: TTableBuilderSetRowColor = useCallback((rowId, property, color) => {
    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const rows: TWebsiteBuilderTableRow[] = [nextSettings.head.row, ...nextSettings.body.rows];
    const row = rows.find(row => row.id === rowId);

    if (row == null || row[property] === color) {
      return;
    }

    row[property] = color;

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const setCellValue: TTableBuilderSetCellValue = useCallback((id, value) => {
    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const rows: TWebsiteBuilderTableRow[] = [nextSettings.head.row, ...nextSettings.body.rows];
    let cell: TWebsiteBuilderTableCell = null;

    for (let i = 0; i < rows.length && cell == null; i++) {
      const row = rows[i];

      cell = row.cells.find(cell => cell.id === id);
    }

    if (cell == null || isEqual(cell.value, value)) {
      return;
    }

    cell.value = cloneDeep(value);

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const clearColumn: TTableBuilderClearColumn = useCallback(columnIndex => {
    const cellCount = tableNodeSettings.head.row.cells.length;

    if (columnIndex < 0 || columnIndex > cellCount - 1) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    nextSettings.head.row.cells[columnIndex].value = { type: "text", content: "" };

    nextSettings.body.rows.forEach(row => {
      row.cells[columnIndex].value = { type: "text", content: "" };
    });

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const clearRow: TTableBuilderClearRow = useCallback(rowId => {
    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const row =
      nextSettings.head.row.id === rowId
        ? nextSettings.head.row
        : nextSettings.body.rows.find(row => row.id === rowId);

    if (row == null) {
      return;
    }

    row.cells.forEach(cell => {
      cell.value = { type: "text", content: "" };
    });

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const deleteColumn: TTableBuilderDeleteColumn = useCallback(columnIndex => {
    const cellCount = tableNodeSettings.head.row.cells.length;

    if (cellCount <= WEBSITE_BUILDER_TABLE_MIN_CELLS || columnIndex < 0 || columnIndex > cellCount - 1) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    nextSettings.head.row.cells.splice(columnIndex, 1);

    nextSettings.body.rows.forEach(row => {
      row.cells.splice(columnIndex, 1);
    });

    setState(prev => ({ ...prev, hoveredColumnIndex: null, selection: null }));
    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const deleteRow: TTableBuilderDeleteRow = useCallback(rowId => {
    const rowIndex = tableNodeSettings.body.rows.findIndex(row => row.id === rowId);

    if (rowIndex === -1) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    nextSettings.body.rows.splice(rowIndex, 1);

    setState(prev => ({ ...prev, hoveredRowId: null, selection: null }));
    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const duplicateColumn: TTableBuilderDuplicateColumn = useCallback(columnIndex => {
    const cellCount = tableNodeSettings.head.row.cells.length;

    if (cellCount >= WEBSITE_BUILDER_TABLE_MAX_CELLS || columnIndex < 0 || columnIndex > cellCount - 1) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    nextSettings.head.row.cells.splice(
      columnIndex + 1,
      0,
      sanitizeCell(nextSettings.head.row.cells[columnIndex]),
    );

    nextSettings.body.rows.forEach(row => {
      row.cells.splice(columnIndex + 1, 0, sanitizeCell(row.cells[columnIndex]));
    });

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const duplicateRow: TTableBuilderDuplicateRow = useCallback(rowId => {
    const rowIndex = tableNodeSettings.body.rows.findIndex(row => row.id === rowId);

    if (rowIndex === -1 || tableNodeSettings.body.rows.length >= WEBSITE_BUILDER_TABLE_MAX_ROWS) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const row = nextSettings.body.rows[rowIndex];
    nextSettings.body.rows.splice(rowIndex + 1, 0, sanitizeBodyRow(row));

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const insertColumn: TTableBuilderInsertColumn = useCallback((columnIndex, placement) => {
    const cellCount = tableNodeSettings.head.row.cells.length;

    if (cellCount >= WEBSITE_BUILDER_TABLE_MAX_CELLS || columnIndex < 0 || columnIndex > cellCount - 1) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const insertionIndex = placement === "after" ? columnIndex + 1 : columnIndex;

    nextSettings.head.row.cells.splice(insertionIndex, 0, generateEmptyHeadCell());

    nextSettings.body.rows.forEach(row => {
      row.cells.splice(insertionIndex, 0, generateEmptyBodyCell());
    });

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const insertRow: TTableBuilderInsertRow = useCallback((rowId, placement) => {
    const rowIndex = tableNodeSettings.body.rows.findIndex(row => row.id === rowId);

    if (rowIndex === -1 || tableNodeSettings.body.rows.length >= WEBSITE_BUILDER_TABLE_MAX_ROWS) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const insertionIndex = placement === "below" ? rowIndex + 1 : rowIndex;
    const cellCount = nextSettings.head.row.cells.length;

    nextSettings.body.rows.splice(insertionIndex, 0, generateEmptyBodyRow(cellCount));

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const insertRowBelowHead: TTableBuilderInsertRowBelowHead = useCallback(() => {
    if (tableNodeSettings.body.rows.length >= WEBSITE_BUILDER_TABLE_MAX_ROWS) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const cellCount = nextSettings.head.row.cells.length;

    nextSettings.body.rows.splice(0, 0, generateEmptyBodyRow(cellCount));

    dispatchTableSettingsUpdate(prevSettings, nextSettings);
  }, [tableNodeSettings, dispatchTableSettingsUpdate]);

  const hoverColumn: TTableBuilderHoverColumn = useCallback(columnIndex => {
    setState(prev => {
      if (prev.hoveredColumnIndex !== columnIndex) {
        return {
          ...prev,
          hoveredColumnIndex: columnIndex,
        };
      } else {
        return prev;
      }
    });
  }, []);

  const hoverRow: TTableBuilderHoverRow = useCallback(rowId => {
    setState(prev => {
      if (prev.hoveredRowId !== rowId) {
        return {
          ...prev,
          hoveredRowId: rowId,
        };
      } else {
        return prev;
      }
    });
  }, []);

  const blurColumn: TTableBuilderBlurColumn = useCallback(columnIndex => {
    setState(prev => {
      if (prev.hoveredColumnIndex === columnIndex) {
        return {
          ...prev,
          hoveredColumnIndex: null,
        };
      } else {
        return prev;
      }
    });
  }, []);

  const blurRow: TTableBuilderBlurRow = useCallback(rowId => {
    setState(prev => {
      if (prev.hoveredRowId === rowId) {
        return {
          ...prev,
          hoveredRowId: null,
        };
      } else {
        return prev;
      }
    });
  }, []);

  const updateHoveredStateAfterMove = useCallback((clientOffset: XYCoord) => {
    const tableDomNode = tableRef.current;

    if (clientOffset == null || tableDomNode == null) {
      hoverRow(null);
      hoverColumn(null);
      return;
    }

    const tableBoundingRect = tableDomNode.getBoundingClientRect();

    const containerRightBoundary = tableBoundingRect.left + containerBoundingBox.width;
    const containerBottomBoundary = tableBoundingRect.top + containerBoundingBox.height;
    
    const intersectsTable =
      clientOffset.x >= tableBoundingRect.left &&
      clientOffset.x <= containerRightBoundary &&
      clientOffset.y >= tableBoundingRect.top &&
      clientOffset.y <= containerBottomBoundary;

    if (!intersectsTable) {
      hoverRow(null);
      hoverColumn(null);
      return;
    }

    const rows: TWebsiteBuilderTableRow[] = [tableNodeSettings.head.row, ...tableNodeSettings.body.rows];

    const hoveredRow = rows.find(row => {
      const rowDomNode = domNodeRefMap.current.get(row.id);
      const rowBoundingRect = rowDomNode?.getBoundingClientRect();

      return (
        rowBoundingRect != null && clientOffset.y >= rowBoundingRect.top && clientOffset.y <= rowBoundingRect.bottom
      );
    });

    const hoveredColumnIndex = tableNodeSettings.head.row.cells
      .map(cell => domNodeRefMap.current.get(cell.id))
      .filter(domNode => domNode != null)
      .sort((prev, next) => prev.getBoundingClientRect().left - next.getBoundingClientRect().left)
      .findIndex(domNode => {
        const boundingRect = domNode.getBoundingClientRect();

        return clientOffset.x >= boundingRect.left && clientOffset.x <= boundingRect.right;
      });

    hoverRow(hoveredRow?.id ?? null);
    hoverColumn(hoveredColumnIndex === -1 ? null : hoveredColumnIndex);
  }, [
    hoverRow,
    hoverColumn,
    containerBoundingBox,
    tableNodeSettings,
  ]);

  const moveColumn: TTableBuilderMoveColumn = useCallback(() => {
    if (dragLayerContext.type !== "column-active" || dragLayerContext.target == null) {
      return;
    }

    const sourceIndex = dragLayerContext.columnIndex;
    const targetIndex = dragLayerContext.target.index;
    
    const cellCount = tableNodeSettings.head.row.cells.length;

    if (
      sourceIndex == null ||
      sourceIndex < 0 ||
      sourceIndex > cellCount - 1 ||
      targetIndex < 0 ||
      targetIndex > cellCount - 1
    ) {
      return;
    }

    const targetInsertionIndex = dragLayerContext.target.anchor === "after" ? targetIndex + 1 : targetIndex;

    const insertionIndex = sourceIndex < targetInsertionIndex ? targetInsertionIndex - 1 : targetInsertionIndex;

    if (sourceIndex === insertionIndex) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const [movedHeadRowCell] = nextSettings.head.row.cells.splice(sourceIndex, 1);

    nextSettings.head.row.cells.splice(insertionIndex, 0, movedHeadRowCell);

    nextSettings.body.rows.forEach(row => {
      const [movedCell] = row.cells.splice(sourceIndex, 1);

      row.cells.splice(insertionIndex, 0, movedCell);
    });

    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, tableNode.id, {
        id: tableNode.id,
        prevContainerSettings: {
          type: "table",
          settings: prevSettings,
        },
        nextContainerSettings: {
          type: "table",
          settings: nextSettings,
        },
      }),
    );

    updateHoveredStateAfterMove(dragLayerContext.clientOffset);
  }, [dragLayerContext, tableNodeSettings, activePageId, deviceView, tableNode.id, updateHoveredStateAfterMove]);

  const moveRow: TTableBuilderMoveRow = useCallback(() => {
    if (dragLayerContext.type !== "row-active" || dragLayerContext.target == null) {
      return;
    }
    
    const rows = tableNodeSettings.body.rows;

    const sourceIndex = rows.findIndex(row => row.id === dragLayerContext.bodyRow.id);
    const targetIndex = rows.findIndex(row => row.id === dragLayerContext.target.id);

    if (sourceIndex === -1 || targetIndex === -1) {
      return;
    }

    const targetInsertionIndex = dragLayerContext.target.anchor === "below" ? targetIndex + 1 : targetIndex;

    const insertionIndex = sourceIndex < targetInsertionIndex ? targetInsertionIndex - 1 : targetInsertionIndex;

    if (sourceIndex === insertionIndex) {
      return;
    }

    const prevSettings = cloneDeep(tableNodeSettings);
    const nextSettings = cloneDeep(tableNodeSettings);

    const [movedRow] = nextSettings.body.rows.splice(sourceIndex, 1);

    nextSettings.body.rows.splice(insertionIndex, 0, movedRow);

    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, tableNode.id, {
        id: tableNode.id,
        prevContainerSettings: {
          type: "table",
          settings: prevSettings,
        },
        nextContainerSettings: {
          type: "table",
          settings: nextSettings,
        },
      }),
    );

    updateHoveredStateAfterMove(dragLayerContext.clientOffset);
  }, [dragLayerContext, tableNodeSettings, activePageId, deviceView, tableNode.id, updateHoveredStateAfterMove]);

  const operations: ITableBuilderOperations = useMemo(() => {
    return {
      setSelection,
      setRowColor,
      setCellValue,
      clearColumn,
      clearRow,
      deleteColumn,
      deleteRow,
      duplicateColumn,
      duplicateRow,
      insertColumn,
      insertRow,
      insertRowBelowHead,
      hoverColumn,
      blurColumn,
      hoverRow,
      blurRow,
      moveColumn,
      moveRow,
    };
  }, [
    setSelection,
    setRowColor,
    setCellValue,
    clearColumn,
    clearRow,
    deleteColumn,
    deleteRow,
    duplicateColumn,
    duplicateRow,
    insertColumn,
    insertRow,
    insertRowBelowHead,
    hoverColumn,
    blurColumn,
    hoverRow,
    blurRow,
    moveColumn,
    moveRow,
  ]);

  return operations;
}
